// Feedback notes (switch: notes). A pencil button on every screen: tap it, tap what the note is
// about, write it. "My notes" lists them with what the notes pass said back.
// Rules live in _shared/notes.ts; the pass itself is .claude/skills/nhub-notes-pass.

import { useCallback, useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { createPortal } from 'react-dom';
import { authorOf, NOTE_STATUS_LABEL, type Note, type NoteStatus, type NoteTarget } from '@shared/notes.ts';
import { Icon } from '../../components/Icon.tsx';
import { Sheet, SheetHeader } from '../../components/ui.tsx';
import { getStore, useAppState, useFeature } from '../../data/store.ts';
import { navigate, usePath } from '../../lib/router.tsx';
import './notes.css';

/** Mounted once by the app; nothing shows unless the switch is on for you. */
export function NotesLayer() {
  return useFeature('notes') ? <NotesControls /> : null;
}

/** Only your own notes: Michael's list doesn't show Natasha's, and hers doesn't show his. */
function useMyNotes(): Note[] {
  const all = useAppState((s) => s.notes);
  const me = useAppState((s) => s.me) ?? 'michael';
  return useMemo(() => all.filter((n) => authorOf(n) === me), [all, me]);
}

type Mode = 'idle' | 'picking' | 'compose' | 'list';

function NotesControls() {
  const path = usePath();
  const notes = useMyNotes();
  const waiting = useMemo(() => notes.filter((n) => n.status === 'needs_you').length, [notes]);
  const [mode, setMode] = useState<Mode>('idle');
  const [route, setRoute] = useState(path);
  const [target, setTarget] = useState<NoteTarget | undefined>();
  const close = useCallback(() => setMode('idle'), []);

  return (
    <>
      {mode === 'idle' && (
        <button
          type="button"
          className="note-fab"
          aria-label={waiting ? `Leave a note (${waiting} need${waiting === 1 ? 's' : ''} you)` : 'Leave a note'}
          onClick={() => {
            setRoute(path);
            setMode('picking');
          }}
        >
          <Icon name="edit" size={20} />
          {waiting > 0 && <span className="note-fab-badge">{waiting}</span>}
        </button>
      )}
      {mode === 'picking' && (
        <PickOverlay
          waiting={waiting}
          onPick={(t) => {
            setTarget(t);
            setMode('compose');
          }}
          onList={() => setMode('list')}
          onCancel={close}
        />
      )}
      {mode === 'compose' && <ComposeSheet route={route} target={target} onRepick={() => setMode('picking')} onClose={close} />}
      {mode === 'list' && <NotesSheet onClose={close} />}
    </>
  );
}

// ------------------------------------------------------------------ picking

/** The nearest thing a person would call "this": a row, a button, a card, a heading. */
const MEANINGFUL = 'a, button, [role="button"], [role="row"], li, tr, label, h1, h2, h3, [aria-label], .card, .list-row';

function clip(s: string | null | undefined, max: number): string | undefined {
  const t = (s ?? '').replace(/\s+/g, ' ').trim();
  if (!t) return undefined;
  return t.length > max ? `${t.slice(0, max - 1)}…` : t;
}

function step(el: Element): string {
  const cls = typeof el.className === 'string' ? el.className.trim().split(/\s+/)[0] : '';
  return el.tagName.toLowerCase() + (cls ? `.${cls}` : '');
}

/** What's at a point on the page, in words the notes pass can find in the code. */
export function describeElement(el: Element): NoteTarget {
  const pick = el.closest(MEANINGFUL) ?? el;
  const root = pick.closest('.layer, main') ?? document.body;
  const path: string[] = [];
  for (let e: Element | null = pick; e && e !== root && path.length < 5; e = e.parentElement) path.unshift(step(e));
  const html = pick as HTMLElement;
  return {
    screen: clip(root.querySelector('h1')?.textContent, 80),
    label: clip(pick.getAttribute('aria-label'), 120),
    text: clip(html.innerText ?? pick.textContent, 200),
    where: [step(root), ...path].join(' > '),
  };
}

function PickOverlay({
  waiting,
  onPick,
  onList,
  onCancel,
}: {
  waiting: number;
  onPick: (t: NoteTarget | undefined) => void;
  onList: () => void;
  onCancel: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState<DOMRect | undefined>();

  /** The page element under the overlay at a point. */
  const under = (x: number, y: number): Element | null => {
    const o = ref.current;
    if (!o) return null;
    o.style.pointerEvents = 'none';
    const el = document.elementFromPoint(x, y);
    o.style.pointerEvents = '';
    return el;
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onCancel();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onCancel]);

  const onMove = (e: ReactPointerEvent) => {
    if (e.pointerType !== 'mouse') return;
    const el = under(e.clientX, e.clientY);
    setBox(el ? (el.closest(MEANINGFUL) ?? el).getBoundingClientRect() : undefined);
  };

  return createPortal(
    <div
      ref={ref}
      className="note-pick"
      onPointerMove={onMove}
      onClick={(e) => {
        if ((e.target as Element).closest('.note-pick-bar')) return;
        const el = under(e.clientX, e.clientY);
        onPick(el && !el.closest('.note-fab') ? describeElement(el) : undefined);
      }}
    >
      {box && <div className="note-pick-box" style={{ left: box.left, top: box.top, width: box.width, height: box.height }} />}
      <div className="note-pick-bar" role="dialog" aria-label="Pick what the note is about">
        <span className="note-pick-hint">Tap what this note is about</span>
        <button type="button" className="chip" onClick={() => onPick(undefined)}>
          Whole screen
        </button>
        <button type="button" className="chip" onClick={onList}>
          My notes{waiting ? ` (${waiting})` : ''}
        </button>
        <button type="button" className="icon-btn" aria-label="Cancel" onClick={onCancel}>
          <Icon name="close" size={18} />
        </button>
      </div>
    </div>,
    document.body,
  );
}

// ------------------------------------------------------------------ writing

function aboutLine(target: NoteTarget | undefined, route: string): string {
  const what = target?.label ?? target?.text;
  const where = target?.screen ?? route;
  return what ? `${what} · ${where}` : `Whole screen · ${where}`;
}

/** Focus a field once its sheet has taken focus for itself. */
function useLateFocus<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  useEffect(() => {
    const f = requestAnimationFrame(() => ref.current?.focus());
    return () => cancelAnimationFrame(f);
  }, []);
  return ref;
}

function ComposeSheet({ route, target, onRepick, onClose }: { route: string; target?: NoteTarget; onRepick: () => void; onClose: () => void }) {
  const [body, setBody] = useState('');
  const field = useLateFocus<HTMLTextAreaElement>();
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    const ok = await getStore().addNote(route, body, target);
    setSaving(false);
    if (ok) onClose();
  };
  return (
    <Sheet title="New note" onClose={onClose}>
      <SheetHeader title="New note" onClose={onClose} />
      <div className="note-about">
        <span className="grow small">{aboutLine(target, route)}</span>
        <button type="button" className="chip" onClick={onRepick}>
          Pick again
        </button>
      </div>
      <div className="field">
        <label htmlFor="note-body">What's wrong, or what you'd like</label>
        <textarea id="note-body" ref={field} className="textarea" rows={4} value={body} onChange={(e) => setBody(e.target.value)} />
      </div>
      <button type="button" className="btn btn-primary" disabled={!body.trim() || saving} onClick={() => void save()}>
        Save note
      </button>
    </Sheet>
  );
}

// ------------------------------------------------------------------ the list

const ORDER: NoteStatus[] = ['needs_you', 'open', 'addressed', 'shipped', 'closed'];
const DONE_SHOWN = 20;

function NotesSheet({ onClose }: { onClose: () => void }) {
  const notes = useMyNotes();
  const groups = useMemo(
    () =>
      ORDER.map((status) => {
        const rows = notes.filter((n) => n.status === status).sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
        return { status, rows: status === 'shipped' || status === 'closed' ? rows.slice(0, DONE_SHOWN) : rows };
      }).filter((g) => g.rows.length),
    [notes],
  );
  return (
    <Sheet title="My notes" onClose={onClose}>
      <SheetHeader title="My notes" subtitle="Run a notes pass from the Claude app to work through the open ones." onClose={onClose} />
      {groups.length === 0 && <p className="muted">No notes yet. Tap the pencil on any screen to leave one.</p>}
      {groups.map((g) => (
        <section key={g.status} className="stack" style={{ gap: 8 }}>
          <span className="eyebrow">{NOTE_STATUS_LABEL[g.status]}</span>
          {g.rows.map((n) => (
            <NoteCard key={n.id} note={n} onGo={onClose} />
          ))}
        </section>
      ))}
    </Sheet>
  );
}

function when(iso: string): string {
  return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function NoteCard({ note, onGo }: { note: Note; onGo: () => void }) {
  const store = getStore();
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState('');
  const start = (initial: string) => {
    setText(initial);
    setEditing(true);
  };
  const done = (action: () => void) => {
    action();
    setEditing(false);
  };

  const form =
    note.status === 'needs_you'
      ? { label: 'Your answer', button: 'Send answer', run: () => store.answerNote(note.id, text), needsText: true }
      : note.status === 'open'
        ? { label: 'Note', button: 'Save', run: () => store.editNote(note.id, text), needsText: true }
        : { label: "What's still wrong (optional)", button: 'Reopen', run: () => store.reopenNote(note.id, text), needsText: false };

  return (
    <div className={`card note-card note-${note.status}`}>
      <div className="row-between small muted">
        <span>{aboutLine(note.target, note.route)}</span>
        <span>{when(note.updatedAt)}</span>
      </div>
      <p className="note-body">{note.body}</p>
      {note.reply && (
        <p className="note-reply">
          <strong>Claude:</strong> {note.reply}
        </p>
      )}
      {note.answer && (
        <p className="note-reply">
          <strong>You:</strong> {note.answer}
        </p>
      )}
      {editing ? (
        <div className="stack" style={{ gap: 8 }}>
          <label className="small" htmlFor={`note-${note.id}`}>
            {form.label}
          </label>
          <textarea id={`note-${note.id}`} className="textarea" rows={3} value={text} onChange={(e) => setText(e.target.value)} />
          <div className="row" style={{ gap: 8 }}>
            <button type="button" className="btn btn-primary" disabled={form.needsText && !text.trim()} onClick={() => done(form.run)}>
              {form.button}
            </button>
            <button type="button" className="btn btn-soft" onClick={() => setEditing(false)}>
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
          {note.status === 'needs_you' && (
            <button type="button" className="chip chip-gold" onClick={() => start('')}>
              Answer
            </button>
          )}
          {note.status === 'open' && (
            <>
              <button type="button" className="chip" onClick={() => start(note.body)}>
                Edit
              </button>
              <button type="button" className="chip" onClick={() => store.deleteNote(note.id)}>
                Delete
              </button>
            </>
          )}
          {(note.status === 'addressed' || note.status === 'shipped' || note.status === 'closed') && (
            <button type="button" className="chip" onClick={() => start('')}>
              Reopen
            </button>
          )}
          <button
            type="button"
            className="chip"
            onClick={() => {
              onGo();
              navigate(note.route, { transition: 'forward' });
            }}
          >
            Go there
          </button>
        </div>
      )}
    </div>
  );
}
